import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { PAGE_GUIDES, WHO_HELP } from "@/data/site";

function PageCard({ guide, index }) {
  return (
    <Reveal delay={(index % 3) * 0.06} className="h-full">
      <article
        data-testid={`help-guide-${guide.id}`}
        className="h-full rounded-3xl border border-ink/5 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-lav-soft/70"
      >
        <span className="font-display text-2xl font-semibold text-lav">{String(index + 1).padStart(2, "0")}</span>
        <h2 className="mt-4 font-display text-xl font-semibold text-ink">{guide.title}</h2>
        <p className="mt-2.5 text-sm leading-relaxed text-ink-soft">{guide.body}</p>
      </article>
    </Reveal>
  );
}

export default function Journaling() {
  return (
    <main data-testid="page-how-it-helps" className="pt-32 sm:pt-40 lg:pt-44">
      <section className="mx-auto max-w-4xl px-5 sm:px-8">
        <div className="text-center">
          <Reveal>
            <Eyebrow tone="mint">How it helps</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1
              className="mt-7 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl lg:text-6xl"
              data-testid="how-it-helps-headline"
            >
              Every page has a job.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink-soft sm:text-lg" data-testid="how-it-helps-sub">
              In Your Corner Healthcare is built around real appointments, real medications, and real questions.
              Here's what each part of the journal does for you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PAGE_GUIDES.map((g, i) => (
              <PageCard key={g.id} guide={g} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <h2
              className="max-w-2xl font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl"
              data-testid="who-helps-headline"
            >
              Made for the people healthcare gets hardest
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {WHO_HELP.map((w, i) => (
              <Reveal key={w.id} delay={i * 0.06} className="h-full">
                <article data-testid={`help-audience-${w.id}`} className="h-full rounded-3xl bg-lav-soft p-7">
                  <h3 className="font-display text-lg font-semibold text-lav-deep">{w.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-ink-soft">{w.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-[2.5rem] bg-lav-soft px-6 py-12 text-center sm:px-12" data-testid="how-it-helps-complement">
              <h2 className="font-display text-2xl font-semibold text-lav-deep sm:text-3xl">
                Complements your care—never replaces it
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-ink-soft sm:text-base">
                In Your Corner Healthcare works alongside MyChart, patient portals, medical records, and your
                care team. It is not medical advice and does not replace your doctor, professional care, or
                emergency services.
              </p>
              <Link
                to="/shop"
                data-testid="how-it-helps-cta-shop"
                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 font-bold text-cream transition-all duration-300 hover:bg-lav-deep"
              >
                See the Journal
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
